import React, { useMemo } from 'react';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { getRoundingStrategy, RoundingStrategy } from '@daedalus/core/src/utils/number';
import { getLinariaClassName } from '../../../utils/getLinariaClassName';
import { Price, PriceSize } from '../Price';
interface Props {
  /** Whether the price should be styled as an anchor price */
  anchorPrice: number;
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** Price currency code */
  currencyCode: string;
  /** The price should be styled as a private deal */
  isPrivateDeal?: boolean;
  /** Whether the price is hidden as it is locked */
  isLockedDeal?: boolean;
  /** Whether the price is an app lock deal */
  isAppLockedDeal?: boolean;
  /** The value to be displayed as the price */
  price: number;
  /** Whether we should show an anchor price */
  showAnchorPrice?: boolean;
  /** Whether the locked icon can be shown. It is only shown when isPrivateDeal is also true. */
  showLockIcon?: boolean;
  /** Whether the locked icon can be shown. It is only shown when isAppLockedDeal is also true. */
  showAppIcon?: boolean;
  /** Whether the elements should be displayed in a row layout */
  showRowLayout?: boolean;
  /** Whether the element should be displayed inline to its siblings ie; not flex */
  shouldDisplayInline?: boolean;
  /** Price Size */
  size?: PriceSize;
  /** Shows the indication by the side of the price */
  showIndication?: boolean;
  /** Shows the indication that the price is total by the side of the price */
  showTotal?: boolean;

  /** Total number of guests */
  numberOfGuests?: number;
  /** Shows the CTAOffer  */
  shouldAlignOffer?: boolean;
  /** Rounding strategy to apply to the price */
  roundingStrategy?: RoundingStrategy;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
}
export const PriceWrapper = /*#__PURE__*/styled('div')({
  name: "PriceWrapper",
  class: "p1h3b732",
  propsAsIs: false
});
const messageWrapperStylesBySize: Record<string, string> = {
  xxl: "x16dgmdk",
  sm: "shaesdz",
  default: "digiugh"
};
const getMessageWrapperStylesBySize = (size?: PriceSize) => {
  if (size !== 'xxl' && size !== 'sm') {
    return messageWrapperStylesBySize.default;
  }
  return messageWrapperStylesBySize[size];
};
export const MessageWrapper = /*#__PURE__*/styled('div')({
  name: "MessageWrapper",
  class: "mugyv1k",
  propsAsIs: false
});
export const PriceAndMessageWrapper = /*#__PURE__*/styled('div')({
  name: "PriceAndMessageWrapper",
  class: "p1416tmm",
  propsAsIs: false
});
export const GuestMessage = /*#__PURE__*/styled('span')({
  name: "GuestMessage",
  class: "g1p2jq0a",
  propsAsIs: false
});
export const PriceComposition = ({
  price,
  anchorPrice,
  currencyCode,
  isPrivateDeal = false,
  isLockedDeal = false,
  showRowLayout,
  shouldDisplayInline = false,
  showAnchorPrice = false,
  showLockIcon = true,
  showAppIcon = false,
  isAppLockedDeal = false,
  size,
  showIndication = false,
  showTotal = false,
  shouldAlignOffer = false,
  roundingStrategy = getRoundingStrategy(),
  className,
  dataId
}: Props) => {
  const renderPriceIndication = useMemo(() => {
    if (showIndication) {
      // Shows the indication that the price is total by the side of the price
      if (showTotal) {
        return <MessageWrapper className={cx(getMessageWrapperStylesBySize(size), '--priceIndication')}>
            <FormattedMessageWrapper id="payment.total" defaultMessage="total" />
          </MessageWrapper>;
      }

      // Shows the indication that the price is per night by the side of the price
      return <MessageWrapper className={cx(getMessageWrapperStylesBySize(size), '--priceIndication')}>
          <FormattedMessageWrapper id="perNight" defaultMessage="/ night" />
        </MessageWrapper>;
    }
    return null;
  }, [size, showIndication, showTotal]);
  return <PriceWrapper className={cx(className, shouldDisplayInline && `${getLinariaClassName(PriceWrapper)}--inline`, `${getLinariaClassName(PriceWrapper)}--${showRowLayout ? 'row' : 'column'}`)}>
      {showAnchorPrice && anchorPrice > 0 && <Price dataId={`Anchor-${dataId}`} isAnchor value={anchorPrice} currency={currencyCode} roundingStrategy={roundingStrategy} shouldAlignOffer={shouldAlignOffer} />}
      <PriceAndMessageWrapper>
        <Price dataId={dataId} value={price} currency={currencyCode} isPrivate={isPrivateDeal} isLocked={isLockedDeal} isAppLocked={isAppLockedDeal} roundingStrategy={roundingStrategy} showLockIcon={showLockIcon} showAppIcon={showAppIcon} size={size} shouldAlignOffer={shouldAlignOffer} />
        {renderPriceIndication}
      </PriceAndMessageWrapper>
    </PriceWrapper>;
};

require("./PriceComposition.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PriceComposition.tsx");